
import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import CategoryFilter from '@/components/CategoryFilter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, ArrowUpAZ, ArrowDownAZ, Grid, Table2, Mail, Download, UserPlus, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface CompanyMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

interface SharedLeadsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  sortDir: 'asc' | 'desc';
  onSortToggle: () => void;
  selectedCategories: string[];
  onCategoriesChange: (cats: string[]) => void;
  availableCategories: string[];
  selectedShareGroups?: string[];
  onShareGroupsChange?: (groups: string[]) => void;
  availableShareGroups?: string[];
  view: 'table' | 'card';
  onViewChange: (view: 'table' | 'card') => void;
  resultCount: number;
  selectedCount?: number;
  onMailMerge?: () => void;
  onExportCSV?: () => void;
  onAssignToUser?: (userId: string, userName: string) => void;
  companyMembers?: CompanyMember[];
  currentUserId?: string;
}

const SharedLeadsToolbar: React.FC<SharedLeadsToolbarProps> = ({
  search,
  onSearchChange,
  sortDir,
  onSortToggle,
  selectedCategories,
  onCategoriesChange,
  availableCategories,
  selectedShareGroups,
  onShareGroupsChange,
  availableShareGroups,
  view,
  onViewChange,
  resultCount,
  selectedCount = 0,
  onMailMerge,
  onExportCSV,
  onAssignToUser,
  companyMembers = [],
  currentUserId,
}) => {
  const getMemberName = (member: CompanyMember) => {
    const name = [member.first_name, member.last_name].filter(Boolean).join(' ');
    return name || member.email || 'Unknown';
  };

  const shareGroupOptions = availableShareGroups || [];
  const hasActiveShareGroups = (selectedShareGroups || []).length > 0;

  return (
    <div className="space-y-3">
      {/* Tier 1 — Filter Bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] max-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by name..."
            className="pl-9 h-8 rounded-full text-xs bg-white/5 border-white/10"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-8 px-3 text-xs bg-white/5 border-white/10 hover:bg-white/10"
          onClick={onSortToggle}
        >
          {sortDir === 'asc' ? <ArrowUpAZ className="h-3.5 w-3.5 mr-1" /> : <ArrowDownAZ className="h-3.5 w-3.5 mr-1" />}
          {sortDir === 'asc' ? 'A → Z' : 'Z → A'}
        </Button>

        {availableCategories.length > 0 && (
          <CategoryFilter
            categories={availableCategories}
            selectedCategories={selectedCategories}
            onSelectCategories={onCategoriesChange}
          />
        )}

        {shareGroupOptions.length > 0 && onShareGroupsChange && selectedShareGroups && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`rounded-full h-8 px-3 text-xs border transition-all ${
                  hasActiveShareGroups
                    ? 'bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30'
                    : 'bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground'
                }`}
              >
                {hasActiveShareGroups
                  ? `${selectedShareGroups.length} group${selectedShareGroups.length > 1 ? 's' : ''}`
                  : 'Share group'}
                <ChevronDown className="ml-1.5 h-3 w-3 shrink-0 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48 max-h-60 overflow-y-auto">
              {shareGroupOptions.map((group) => {
                const isSelected = selectedShareGroups.includes(group);
                return (
                  <DropdownMenuItem
                    key={group}
                    onClick={() => {
                      if (isSelected) {
                        onShareGroupsChange(selectedShareGroups.filter(g => g !== group));
                      } else {
                        onShareGroupsChange([...selectedShareGroups, group]);
                      }
                    }}
                    className="cursor-pointer"
                  >
                    <span className={isSelected ? 'font-medium text-foreground' : 'text-muted-foreground'}>
                      {group}
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
            <Button
              variant={view === 'table' ? 'default' : 'ghost'}
              size="icon"
              className="h-7 w-7"
              onClick={() => onViewChange('table')}
            >
              <Table2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant={view === 'card' ? 'default' : 'ghost'}
              size="icon"
              className="h-7 w-7"
              onClick={() => onViewChange('card')}
            >
              <Grid className="h-3.5 w-3.5" />
            </Button>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {resultCount} result{resultCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Tier 2 — Conditional Action Bar */}
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl glass-card animate-in fade-in slide-in-from-top-2 duration-200">
          <Badge variant="secondary" className="text-xs">
            {selectedCount} selected
          </Badge>

          {onMailMerge && (
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onMailMerge}>
              <Mail className="h-3.5 w-3.5" />
              Mail Merge
            </Button>
          )}

          {onExportCSV && (
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onExportCSV}>
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </Button>
          )}

          {onAssignToUser && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
                  <UserPlus className="h-3.5 w-3.5" />
                  Assign
                  <ChevronDown className="h-3 w-3 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 max-h-72 overflow-y-auto">
                {companyMembers.map((member) => {
                  const name = getMemberName(member);
                  const isCurrentUser = member.id === currentUserId;
                  return (
                    <DropdownMenuItem
                      key={member.id}
                      onClick={() => onAssignToUser(member.id, name)}
                      className="flex flex-col items-start gap-0.5 cursor-pointer"
                    >
                      <span className="text-sm font-medium">
                        {name}{isCurrentUser ? ' (Me)' : ''}
                      </span>
                      {member.email && (
                        <span className="text-xs text-muted-foreground">{member.email}</span>
                      )}
                    </DropdownMenuItem>
                  );
                })}
                {companyMembers.length === 0 && (
                  <DropdownMenuItem disabled>
                    <span className="text-sm text-muted-foreground">No team members found</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  );
};

export default SharedLeadsToolbar;
